import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ChartDataFragment } from '@client/graphql/__generated__/types';
import { GradientBorder } from '~/common/components/GradientBorder/GradientBorder';
import { useIsDataCoachEnabled } from '~/common/copilot/hooks/isChatEnabled';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { ListingReportChartType } from '../../ListingPageContext';
import { ChatTriggerIcon } from '../../ReportChat/ChatTriggerIcon';
import { ListingChatModalWrapper } from '../../ReportChat/ListingChatModalWrapper';
import { getValueFormatter } from '../Charts/chart.utils';
import { MetricBarChart } from '../Charts/MetricBarChart';
import { MetricChartLegend } from '../Charts/MetricChartLegend';
import { MetricLineChart } from '../Charts/MetricLineChart';
import { METRIC_CARD_MIN_HEIGHT } from '../constants';
import { EnabledIndigoDataTag } from './EnabledIndigoDataTag';
import { MetricCardEmptyState } from './MetricCardEmptyState';
import { MetricTrendTag } from './MetricTrendTag';

interface ListingPageMetricCardProps {
  title: string;
  isViewOnly: boolean;
  metricData?: ChartDataFragment;
  showEmptyState?: boolean;
  includeIndigoData?: boolean;
}

export const ListingPageMetricCard = ({
  title,
  isViewOnly,
  metricData,
  showEmptyState,
  includeIndigoData,
}: ListingPageMetricCardProps) => {
  const isMobile = useIsMobile();
  const isDataCoachEnabled = useIsDataCoachEnabled();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const mainMetric = metricData?.mainMetric;

  const mainMetricPercent = getValueFormatter({
    unit: mainMetric?.unit,
    short: false,
  })?.(mainMetric?.value, 0);

  const mainMetricTrend = mainMetric?.trend?.value;
  const mainMetricTrendPeriod = mainMetric?.trend?.period;

  const Chart =
    metricData?.type === ListingReportChartType.LINE
      ? MetricLineChart
      : MetricBarChart;

  const ChartComponentForModal = metricData ? (
    <Chart
      data={metricData?.data}
      labelUnit={metricData?.labelUnit}
      showLegend={metricData?.styleConfig?.showLegend}
      valueUnit={metricData?.valueUnit}
      verticalReferenceLine={metricData?.verticalReferenceLine}
    />
  ) : null;

  const TrendComponent = (
    <GradientBorder
      alignItems="center"
      direction="column"
      flexBasis={0}
      flexGrow={3}
      gap={2}
      justifyContent="center"
      minWidth={{ base: '100%', md: '220px' }}
      p={5}
      width={{ base: '100%', md: 'auto' }}
    >
      <Text fontSize="40px" fontWeight="medium" lineHeight="1">
        {mainMetricPercent}
      </Text>
      <Text
        color="whiteAlpha.700"
        fontSize="14px"
        lineHeight="normal"
        mb={2}
        textAlign="center"
      >
        {mainMetric?.description}
      </Text>
      {!!mainMetricTrend && (
        <MetricTrendTag
          trend={mainMetricTrend}
          trendPeriod={mainMetricTrendPeriod}
          unit={mainMetric?.unit}
        />
      )}
    </GradientBorder>
  );

  const OverviewComponent = (
    <VStack gap={1} h="full">
      <Text fontSize="14px" fontWeight="medium">
        {title}
      </Text>
      {TrendComponent}
      <Box flexGrow={1} w="full">
        {ChartComponentForModal}
      </Box>
    </VStack>
  );

  const initialPrompt = `How does the ${title} market data impact the listing, and what are the key takeaways for my client?`;

  return (
    <>
      <Card
        _hover={{ '& .ChatTriggerIcon': { opacity: 1 } }}
        minHeight={METRIC_CARD_MIN_HEIGHT}
        position="relative"
      >
        {!showEmptyState && !isViewOnly && !isMobile && isDataCoachEnabled && (
          <ChatTriggerIcon
            opacity={0}
            position="absolute"
            right={2}
            top={2}
            zIndex={1}
            onOpen={onOpen}
          />
        )}
        <CardHeader>
          <Flex
            alignItems={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={2}
            justifyContent={{ base: 'center', md: 'space-between' }}
          >
            <Text fontWeight="medium">{title}</Text>
            {!includeIndigoData && !showEmptyState && <EnabledIndigoDataTag />}
          </Flex>
        </CardHeader>
        {showEmptyState ? (
          <MetricCardEmptyState />
        ) : (
          <CardBody>
            <Flex
              alignItems="center"
              direction="column"
              gap={2}
              height="100%"
              minHeight="0px"
              minWidth="0px"
            >
              <Flex
                alignItems="flex-start"
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: 4, md: 2 }}
                height="100%"
                minHeight="0px"
                minWidth="0px"
                width="100%"
              >
                {TrendComponent}

                <Flex
                  flexBasis={0}
                  flexGrow={5}
                  height="100%"
                  minHeight={{ base: '160px', md: 'auto' }}
                  width={{ base: '100%', md: 'auto' }}
                >
                  <Flex
                    alignItems="center"
                    direction="column"
                    gap={1}
                    height="100%"
                    width="100%"
                  >
                    <Text
                      color="whiteAlpha.700"
                      fontSize="14px"
                      fontWeight="medium"
                    >
                      {metricData?.title}
                    </Text>
                    <Chart
                      data={metricData?.data}
                      labelUnit={metricData?.labelUnit}
                      showLegend={metricData?.styleConfig?.showLegend}
                      valueUnit={metricData?.valueUnit}
                      verticalReferenceLine={metricData?.verticalReferenceLine}
                    />
                  </Flex>
                </Flex>
              </Flex>
              {!!metricData?.styleConfig?.showLegend && (
                <MetricChartLegend data={metricData?.data} />
              )}
            </Flex>
          </CardBody>
        )}
      </Card>
      <ListingChatModalWrapper
        initialPrompt={initialPrompt}
        isOpen={isOpen}
        refContent={OverviewComponent}
        onClose={onClose}
      />
    </>
  );
};
